import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { CASE_STUDIES } from "../data/portfolio";

export default function PortfolioDetail() {
  const { slug } = useParams();
  const study = CASE_STUDIES.find((c) => c.slug === slug);
  if (!study) return <Navigate to="/portfolio" replace />;

  return (
    <div data-testid="portfolio-detail-page" className="pt-32 md:pt-44">
      <Seo
        title={`${study.client} — Case Study | Quexify`}
        description={study.summary}
        path={`/portfolio/${study.slug}`}
      />

      <section className="max-w-[900px] mx-auto px-6 md:px-10">
        <Reveal>
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-neutral-500 hover:text-black transition-colors mb-10" data-testid="back-to-portfolio">
            <ArrowLeft size={18} /> Case studies
          </Link>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-neutral-400">
            <span>{study.tag}</span>
            <span className="h-1 w-1 rounded-full bg-neutral-300" />
            <span>{study.location}</span>
          </div>
          <h1 className="mt-6 font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95]">{study.client}</h1>
          <p className="mt-6 font-display text-2xl md:text-3xl tracking-tight text-neutral-500">{study.result}</p>
        </Reveal>
      </section>

      {/* Metrics */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 mt-14 md:mt-20">
        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 rounded-3xl bg-[#0a0a0a] text-white p-8 md:p-12" data-testid="detail-metrics">
            {study.metrics.map((m) => (
              <div key={m.k}>
                <div className="font-display text-4xl md:text-5xl tracking-tighter font-medium tabular-nums">{m.v}</div>
                <div className="mt-2 text-sm text-neutral-400">{m.k}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Story */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 mt-16 md:mt-24 space-y-8">
        {study.full.map((p, i) => (
          <Reveal key={i} delay={0.03}>
            <p className="text-xl md:text-2xl text-neutral-600 leading-relaxed">{p}</p>
          </Reveal>
        ))}
      </section>

      {/* Quote */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 mt-16 md:mt-24">
        <Reveal>
          <blockquote className="border-l-2 border-black pl-8" data-testid="detail-quote">
            <p className="font-display text-3xl md:text-4xl tracking-tight font-medium leading-[1.1]">"{study.quote}"</p>
            <footer className="mt-6 text-neutral-500">{study.author}</footer>
          </blockquote>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 mt-20 mb-24 md:mb-40">
        <Reveal>
          <div className="rounded-3xl bg-[#fafafa] border border-black/5 px-8 md:px-14 py-14 text-center">
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium leading-[0.98] max-w-2xl mx-auto">
              Your story is the next one.
            </h2>
            <Link to="/book-demo" className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#0a0a0a] text-white px-9 py-4 text-lg transition-transform duration-300 hover:-translate-y-0.5" data-testid="detail-cta">
              Book a discovery call <ArrowUpRight size={20} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
